import { FormattedText } from "@faire/deprecated-retailer-visitor-shared/components/FormattedText";
import { GhostBlock } from "@faire/deprecated-retailer-visitor-shared/components/GhostLoading/GhostBlock";
import { TrackedLink } from "@faire/deprecated-retailer-visitor-shared/components/TrackedLink/index";
import { OVERLAY_EVENT_TOKEN } from "@faire/deprecated-retailer-visitor-shared/features/InsightsFeed/events";
import { InsightCardContext } from "@faire/deprecated-retailer-visitor-shared/features/InsightsFeed/InsightCardContext";
import { InsightCardProduct } from "@faire/deprecated-retailer-visitor-shared/features/InsightsFeed/InsightCardProduct";
import { InsightCardProductImage } from "@faire/deprecated-retailer-visitor-shared/features/InsightsFeed/InsightCardProductImage";
import { MuteModal } from "@faire/deprecated-retailer-visitor-shared/features/InsightsFeed/MuteModal";
import { getIsApparelRetailer } from "@faire/deprecated-retailer-visitor-shared/serialized-data/getIsApparelRetailer";
import { Core } from "@faire/design-tokens";
import { Avatar } from "@faire/slate/components/avatar";
import { Close } from "@faire/slate/icons/Close";
import { IInsightTile } from "@faire/web-api--source/indigofair/data/IInsightTile";
import brandPage from "@faire/web-api--source/routes/www/brand/brandToken";
import { localNum } from "@faire/web/common/localization";
import { MediumFormatting } from "@faire/web/common/localization/formatting";
import { Button } from "@faire/web/slate/Button";
import { Opacity } from "@faire/web/slate/Color";
import { AnnouncementMuted } from "@faire/web/slate/icons/foundation/Announcement";
import { Column, Grid, Row } from "@faire/web/slate/Layout";
import { getSpacing, GrowingSpacer, Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { Link } from "@faire/web/ui/LinkAnchor";
import { TransparencyOverlay } from "@faire/web/ui/TransparencyOverlay";
import { ColorSpan } from "@faire/web/ui/Typography/Span";
import range from "lodash/range";
import truncate from "lodash/truncate";
import React, { useContext } from "react";
import styled, { css } from "styled-components";

interface IProps {
  data: IInsightTile | undefined;
  asCarouselTile?: boolean;
}

const COMPACT_GAP = 2;
const COMPACT_COLUMNS = 3;
const REGULAR_GAP = 16;
const REGULAR_COLUMNS = 4;

export const InsightCard: React.FC<IProps> = (props) => {
  const { data, asCarouselTile } = props;
  const { isMobile } = useViewport();
  const [muteModalOpen, setMuteModalOpen] = React.useState(false);
  const {
    containerOrigin,
    onVisible,
    onBrandClick,
    onOverlayClick,
    mutedBrands,
    onChangeMute,
  } = useContext(InsightCardContext);
  const compact = asCarouselTile || isMobile;
  const gap = compact ? COMPACT_GAP : REGULAR_GAP;
  const columns = compact ? COMPACT_COLUMNS : REGULAR_COLUMNS;

  const brandToken = data?.brand_token ?? "";

  const onVisibleRef = useDoActionWhenVisible(() =>
    onVisible?.(data?.insight_token ?? "")
  );

  if (!data) {
    return (
      <GhostInsightCard
        gap={gap}
        columns={columns}
        asCarouselTile={asCarouselTile}
      />
    );
  }

  const isMuted = (mutedBrands ?? []).includes(data.brand_token ?? "");
  const displayedProducts =
    (data.product_count ?? 0) > columns ? columns - 1 : columns;
  const extraProducts = Math.max(
    (data.product_count ?? 0) - displayedProducts,
    0
  );

  const renderDescription = () =>
    asCarouselTile ? (
      <DescriptionContainer>
        <Description>{data.insight_description_text}</Description>
      </DescriptionContainer>
    ) : (
      <Typography>{data.insight_description_text}</Typography>
    );

  const handleMuteClick = () => {
    setMuteModalOpen(true);
  };

  const renderMuteModal = () =>
    muteModalOpen ? (
      <MuteModal
        brandName={data.brand_name ?? ""}
        brandToken={brandToken}
        isMuted={isMuted}
        onClose={() => setMuteModalOpen(false)}
        containerOrigin={containerOrigin ?? ""}
        onChangeMute={onChangeMute}
      />
    ) : null;

  if (isMuted) {
    return (
      <MutedCard>
        {renderMuteModal()}
        <MutedCardContent>
          <AnnouncementMuted fontSize="24px" />
          <Spacer height="1x" />
          <Typography variant="paragraphSansMedium" align="center">
            <StrictLocalMsg
              id="NwBKrQ"
              defaultMessage="Updates muted"
              description={{
                text: "Toast message shown to user after successfully change the user preference",
                img: "https://cdn.faire.com/fastly/27e58ef4b23c3e9b21fff2ef9fb31da66defd7f63926c67754009c6091e6646d.png",
              }}
            />
          </Typography>
          <Spacer height="1x" />
          <Typography color={Core.text.subdued} align="center">
            <StrictLocalMsg
              id="vQOqa4"
              defaultMessage="You’ll no longer get updates from <MediumFormatting>{brandCompanyName}</MediumFormatting>."
              values={{
                brandCompanyName: truncate(data.brand_name ?? ""),
                MediumFormatting,
              }}
              description={{
                text: "Message informing user that a brand has been muted",
                img: "https://cdn.faire.com/fastly/4699e53715317dfa04d07a67b4dbb565e2fdacf7438358772d5ee220312567aa.png",
              }}
            />
          </Typography>
          <Spacer height="1x" />
          <Button variant="tertiary" onClick={handleMuteClick}>
            <StrictLocalMsg
              id="VtA/3Z"
              defaultMessage="Undo"
              description={{
                text: "Button to undo the recent paste by the user",
              }}
            />
          </Button>
        </MutedCardContent>
      </MutedCard>
    );
  }

  return (
    <Wrapper $asCarouselTile={asCarouselTile} ref={onVisibleRef}>
      {renderMuteModal()}
      <Row>
        <BrandLink
          token={data.brand_token ?? ""}
          onClick={() => onBrandClick?.(data.brand_token ?? "")}
          to={brandPage(data.brand_token ?? "")}
          data-test-id="insightTile-brand-link"
        >
          <Avatar image={data.brand_profile_image} size="xSmall" />
          <Spacer width="1x" />
          <Row align="center">
            <Typography
              maxLines={
                asCarouselTile && getIsApparelRetailer() ? 2 : undefined
              }
            >
              <FormattedText text={data.insight_title} />{" "}
              <Graphite>{data.insight_time_text}</Graphite>
            </Typography>
          </Row>
        </BrandLink>
        <GrowingSpacer base="2x" />
        <MuteButton onClick={handleMuteClick}>
          <Close color={Core.icon.subdued} />
        </MuteButton>
      </Row>
      <GrowingSpacer base="2x" />
      {data.insight_description_text ? (
        renderDescription()
      ) : (
        <Grid gap={gap} columns={columns}>
          {data.products.slice(0, columns).map((product, index, arr) =>
            index === arr.length - 1 && extraProducts > 0 ? (
              <OverlayLink
                key={index}
                to={data.overlay_cta_link ?? brandPage(data.brand_token ?? "")}
                data-test-id="insightTile-overlay-link"
                onClick={() =>
                  onOverlayClick?.(OVERLAY_EVENT_TOKEN, data.overlay_cta_link)
                }
              >
                <InsightCardProductImage
                  image={product.image}
                  asCarouselTile={!!asCarouselTile}
                  shouldShowVerticalImagery={
                    !!data.should_show_vertical_imagery
                  }
                  isCompact={compact}
                />

                <StyledOverlay>
                  {extraProducts > 9 ? (
                    <Typography
                      variant="subheadingSansMedium"
                      color={Core.text.inverse}
                      data-test-id="insightTile-extra-products-count"
                    >
                      <StrictLocalMsg
                        id="1PVM32"
                        defaultMessage="Shop all"
                        description={{
                          text: "Button navigating to a list with all items of a given type",
                        }}
                      />
                    </Typography>
                  ) : (
                    <Typography
                      variant="sectionHeaderSansMedium"
                      color={Core.text.inverse}
                      data-test-id="insightTile-extra-products-count"
                    >
                      <StrictLocalMsg
                        id="IlbPF7"
                        defaultMessage="+{countNumber}"
                        description={{
                          text: "A counter showing the number of extra products in a list",
                          img: "https://cdn.faire.com/fastly/f037b34cba4e7fac281649303451bc64211498377b9db0c3076da1479e8d3daa.png",
                        }}
                        values={{ countNumber: localNum(extraProducts) }}
                      />
                    </Typography>
                  )}
                </StyledOverlay>
              </OverlayLink>
            ) : (
              <InsightCardProduct
                brandToken={data.brand_token ?? ""}
                product={product}
                key={product.product_token ?? index}
                asCarouselTile={!!asCarouselTile}
                shouldShowVerticalImagery={!!data.should_show_vertical_imagery}
                isCompact={compact}
              />
            )
          )}
        </Grid>
      )}
    </Wrapper>
  );
};

const Graphite = ColorSpan(Core.text.subdued);

const Wrapper = styled(Column)<{ $asCarouselTile?: boolean }>`
  width: 100%;
  ${(props) =>
    props.$asCarouselTile
      ? css`
          padding: ${getSpacing("2x")};
          border: 1px solid ${Core.border.muted};
        `
      : css`
          ${responsiveMediaQueries.tabletAndBelow} {
            padding: ${getSpacing(0, "2x")};
          }
        `}
`;

const MutedCard = styled(Column).attrs({ align: "center", justify: "center" })`
  background-color: ${Core.surface.subdued};
  border: 1px solid ${Core.border.muted};
  padding: ${getSpacing("2x", "4x")};
  height: 100%;
`;

const MutedCardContent = styled(Column).attrs({ align: "center" })`
  max-width: 312px;
`;

/**
 * @trackcomponent
 * @supersededBy "@faire/web/slate/IconButton/IconButton#IconButton"
 */
const MuteButton = styled.button`
  display: flex;
  align-self: flex-start;
  &:focus-visible {
    outline: 2px solid -webkit-focus-ring-color;
  }
`;

const BrandLink = styled(TrackedLink)`
  display: flex;
  &:focus-visible {
    outline: 2px solid -webkit-focus-ring-color;
  }
`;

const OverlayLink = styled(Link)`
  position: relative;
  flex: 1;
  margin: 2px;

  &:focus-visible {
    outline: 2px solid -webkit-focus-ring-color;
  }
`;

const StyledOverlay = styled(TransparencyOverlay)`
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${Core.surface.enabled + Opacity.O_35};
  text-align: center;
  padding: 0 4px;
  hyphens: auto;
`;

// We're doing this css hack to ensure the description text is aligned with
// images on other tiles. This will create a container simulating the same
// size of the images while keeping its aspect ration, then add the text inside
const DescriptionContainer = styled.div`
  width: 100%;
  padding-top: calc(
    (100% - ${(COMPACT_COLUMNS - 1) * COMPACT_GAP}px) / ${COMPACT_COLUMNS}
  );
  position: relative;
`;

const Description = styled(Typography)`
  position: absolute;
  top: 0;
  left: 0;
`;

const GhostInsightCard: React.FC<{
  gap: number;
  columns: number;
  asCarouselTile: boolean | undefined;
}> = ({ gap, columns, asCarouselTile }) => (
  <Wrapper data-test-id="insight-ghost-tile" $asCarouselTile={asCarouselTile}>
    <Row align="center">
      <GhostBlock
        $width="40px"
        $height="40px"
        style={{ borderRadius: "100%", flexShrink: 0 }}
      />
      <Spacer width="1x" />
      <Column style={{ flexGrow: 1 }}>
        <GhostBlock $height="14px" />
        <Spacer height="1x" />
        <GhostBlock $height="14px" $width="50%" />
      </Column>
    </Row>
    <Spacer height="2x" />
    <Grid gap={gap} columns={columns}>
      {range(columns).map((i) => (
        <GhostBlock style={{ paddingTop: "100%" }} key={i} />
      ))}
    </Grid>
  </Wrapper>
);
